import { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { 
  FaEnvelope, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaShieldAlt,
  FaArrowRight
} from "react-icons/fa";

// Color palette based on ABM Group colors
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

const AdminLogin = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!identifier || !password) {
      setError("Please enter identifier and password");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const res = await axios.post("/auth/login", { identifier, password });
      
      // Store in localStorage if remember me is checked
      if (rememberMe) {
        localStorage.setItem("adminRememberedIdentifier", identifier);
      } else {
        localStorage.removeItem("adminRememberedIdentifier");
      }
      // Verify admin role
      if (res.data?.user?.role !== "admin") {
        setError("Access denied. Admin privileges required.");
        return;
      }
      
      login(res.data);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Load remembered identifier on component mount
  useEffect(() => {
    const rememberedIdentifier = localStorage.getItem("adminRememberedIdentifier");
    if (rememberedIdentifier) {
      setIdentifier(rememberedIdentifier);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen mt-24 flex items-center justify-center relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${colors.darkBlue}, ${colors.navyBlue}, ${colors.tealBlue})`
      }}
    >
      {/* Decorative circles */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-10"
        style={{ backgroundColor: colors.skyBlue, transform: 'translate(-30%, -30%)' }}></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10"
        style={{ backgroundColor: colors.brightBlue, transform: 'translate(30%, 30%)' }}></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full opacity-5"
        style={{ backgroundColor: colors.lightCyan, transform: 'translate(-50%, -50%)' }}></div>

      {/* Main Login Card */}
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="bg-white/95 backdrop-blur-sm shadow-2xl rounded-2xl p-8 border border-white/30">
          
          {/* Header with gradient */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: colors.pinkMagenta }}>A</div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl"
                style={{ backgroundColor: colors.freshGreen }}>B</div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: colors.goldenYellow }}>M</div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-2">
              <FaShieldAlt style={{ color: colors.deepOrange }} />
              <h2 className="text-3xl font-bold" style={{ color: colors.nearBlack }}>
                Admin Portal
              </h2>
            </div>
            <p className="text-sm mt-2" style={{ color: colors.oliveBrown }}>
              Secure access for administrators only
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm text-white"
              style={{ backgroundColor: colors.reddishBrown }}>
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            
            {/* Identifier Field */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                Admin Email or Unique ID
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <FaEnvelope style={{ color: colors.brightBlue }} />
                </div>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Enter admin email or unique ID"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border transition focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: error && !identifier ? colors.reddishBrown : '#e2e8f0',
                    focusRingColor: colors.deepOrange
                  }}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                Admin Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <FaLock style={{ color: colors.deepOrange }} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your admin password"
                  className="w-full pl-10 pr-10 py-3 rounded-lg border transition focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: error && !password ? colors.reddishBrown : '#e2e8f0',
                    focusRingColor: colors.deepOrange
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? 
                    <FaEyeSlash style={{ color: colors.oliveBrown }} /> : 
                    <FaEye style={{ color: colors.oliveBrown }} />
                  }
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 focus:ring-0"
                  style={{ accentColor: colors.deepOrange }}
                />
                <span className="text-sm" style={{ color: colors.oliveBrown }}>
                  Remember me
                </span>
              </label>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-white transition transform hover:-translate-y-1 hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ 
                background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})`
              }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Authenticating...
                </>
              ) : (
                <>
                  Access Admin Panel <FaArrowRight />
                </>
              )}
            </button>
          </form>

          {/* Back to User Login */}
          <p className="text-center text-sm mt-6" style={{ color: colors.oliveBrown }}>
            <Link 
              to="/login" 
              className="font-semibold hover:underline"
              style={{ color: colors.freshGreen }}
            >
              ← Back to User Login
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
